import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScanIcon } from '../../../../../components/Icons';
import { convertSecondsToTime } from '../../../../../utils/timeFormat';
import { StationVolumeSlider } from '../StationVolumeSlider/StationVolumeSlider';

interface StationFinalViewProps {
  title: string;
  coverUrl?: string;
  qrCodeDataUrl: string;
  isSongReady?: boolean;
  isPlaying: boolean;
  isBuffering?: boolean;
  duration: number;
  currentTime: number;
  displayDuration: string;
  waveformData: number[];
  progress: number;
  volume: number;
  onVolumeChange: (volume: number) => void;
  onPlayPause: () => void;
  onSeek: (percentage: number) => void;
  hasMultipleVariations?: boolean;
  hideAudioControls?: boolean;
}

const isValidTime = (value: number): boolean => {
  return isFinite(value) && value >= 0 && value <= 3600;
};

export const StationFinalView = ({
  title,
  coverUrl,
  qrCodeDataUrl,
  isSongReady,
  isPlaying,
  isBuffering = false,
  duration,
  currentTime,
  displayDuration,
  waveformData,
  progress,
  volume,
  onVolumeChange,
  onPlayPause,
  onSeek,
  hasMultipleVariations = false,
  hideAudioControls = false,
}: StationFinalViewProps) => {
  const { t } = useTranslation();
  const qrCodeContainerRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="relative mt-[23px] mb-[23px] overflow-visible mr-0 w-full">
        <div
          className="absolute inset-0 pointer-events-none rounded-[24px] p-[1.567px] z-0"
          style={{
            background:
              'linear-gradient(to bottom, #666 0%, #666 30%, rgba(102, 102, 102, 0.6) 60%, rgba(102, 102, 102, 0.2) 80%, transparent 100%)',
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMask:
              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'exclude',
            WebkitMaskComposite: 'xor',
          }}
        />
        <div className="w-full overflow-hidden relative z-10 rounded-[24px] bg-[rgba(0,0,1,0.25)]">
          <div className="p-6">
            <h2 className="mb-4 text-left text-white font-['DM_Sans'] text-[26px] font-medium leading-[40px] tracking-[-0.52px]">
              {title}
            </h2>
            {qrCodeDataUrl && (
              <div
                ref={qrCodeContainerRef}
                className="w-full aspect-square rounded-xl mb-6 overflow-hidden relative bg-white select-none">
                <img
                  src={qrCodeDataUrl}
                  alt={t('share.qrCode')}
                  className="w-full h-full object-cover"
                />
                {coverUrl && (
                  <div className="absolute select-none top-1/2 left-1/2 w-1/4 h-1/4 -translate-x-1/2 -translate-y-1/2 p-2 bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
                    <img
                      src={coverUrl}
                      alt={t('generation.albumCover')}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                )}
              </div>
            )}
            {!hideAudioControls && (
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={onPlayPause}
                  className="w-14 h-14 flex items-center justify-center transition-opacity shrink-0">
                  {isBuffering ? (
                    <div className="w-14 h-14 flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : isPlaying ? (
                    <svg
                      width="56"
                      height="56"
                      viewBox="0 0 56 56"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <rect width="56" height="56" rx="28" fill="white" />
                      <rect
                        x="20"
                        y="18"
                        width="5"
                        height="20"
                        rx="1"
                        fill="black"
                      />
                      <rect
                        x="31"
                        y="18"
                        width="5"
                        height="20"
                        rx="1"
                        fill="black"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="56"
                      height="56"
                      viewBox="0 0 56 56"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <rect width="56" height="56" rx="28" fill="white" />
                      <path
                        d="M37.1548 26.6673L22.834 17.7168C21.7873 17.0626 20.4297 17.8151 20.4297 19.0494V36.9504C20.4297 38.1847 21.7873 38.9372 22.834 38.283L37.1548 29.3325C38.1396 28.717 38.1396 27.2828 37.1548 26.6673Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </button>
                <div className="flex-[2] flex items-center gap-2 relative h-14">
                  <div
                    className="flex-1 flex items-center justify-start h-full relative overflow-hidden rounded-lg bg-black/20 px-2 cursor-pointer"
                    onClick={(e) => {
                      if (!duration) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percentage = (x / rect.width) * 100;
                      onSeek(Math.max(0, Math.min(100, percentage)));
                    }}>
                    {waveformData.length > 0
                      ? waveformData.map((height, i) => {
                          const totalBars = waveformData.length;
                          const progressBar = Math.floor(
                            (progress / 100) * totalBars,
                          );
                          const isPlayed = i < progressBar;
                          return (
                            <div
                              key={i}
                              className={`rounded-sm flex-shrink-0 ${
                                isPlayed ? 'bg-pink-500' : 'bg-pink-500/30'
                              }`}
                              style={{
                                width: `${100 / totalBars}%`,
                                height: `${height}px`,
                                minHeight: '4px',
                                maxHeight: '100%',
                              }}
                            />
                          );
                        })
                      : Array.from({ length: 60 }, (_, i) => (
                          <div
                            key={i}
                            className="bg-pink-500/30 rounded-sm flex-shrink-0"
                            style={{
                              width: `${100 / 60}%`,
                              height: '15px',
                              minHeight: '4px',
                            }}
                          />
                        ))}
                  </div>
                </div>
                <span className="min-w-[48px] text-left shrink-0 overflow-hidden text-white text-ellipsis font-['DM_Sans'] text-xl font-medium leading-6 tracking-[-0.6px] line-clamp-1">
                  {duration > 0 && isValidTime(currentTime)
                    ? convertSecondsToTime(Math.floor(currentTime))
                    : displayDuration}
                </span>
                {isSongReady && (
                  <StationVolumeSlider
                    volume={volume}
                    onVolumeChange={onVolumeChange}
                    containerRef={qrCodeContainerRef}
                    inline={true}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center gap-3 mt-6">
        <ScanIcon />
        <div className="text-white text-center font-['Season_Serif-TRIAL'] text-[36px] font-[470] leading-[48px] tracking-[-0.72px]">
          <div className="select-none">{t('generation.scanTheCode')}</div>
          <div className="select-none">{t('generation.toShareTheSong')}</div>
        </div>
      </div>
      {hasMultipleVariations && (
        <div className="mt-6 text-center text-white/60 font-['DM_Sans'] text-lg font-normal leading-6 tracking-[-0.36px] select-none">
          {t('generation.swipeToViewVariations')}
        </div>
      )}
    </>
  );
};
